// src/pages/operations-dashboard/components/NdrCodSection.jsx
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { AlertTriangle, Banknote, ShieldAlert, Clock, CheckCircle2, XCircle } from "lucide-react";
import {
  formatCompact,
  formatPercent,
  formatCurrency,
  CHART_COLORS,
} from "../../../utils/dashboardConstants";

const NDR_COLORS = {
  PENDING_ACTION: { color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-600", icon: Clock },
  ACTION_TAKEN: { color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-600", icon: CheckCircle2 },
  RESOLVED: { color: "#10b981", bg: "bg-emerald-50", text: "text-emerald-600", icon: CheckCircle2 },
  ESCALATED: { color: "#ef4444", bg: "bg-red-50", text: "text-red-600", icon: ShieldAlert },
  CLOSED: { color: "#6b7280", bg: "bg-gray-100", text: "text-gray-600", icon: XCircle },
};

const COD_COLORS = {
  PENDING: { color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-600" },
  IN_PROCESS: { color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-600" },
  REMITTED: { color: "#10b981", bg: "bg-emerald-50", text: "text-emerald-600" },
  ON_HOLD: { color: "#ef4444", bg: "bg-red-50", text: "text-red-600" },
};

const GenericTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.payload.fill || d.color }} />
        <span className="text-sm font-semibold text-gray-800">{d.name || d.payload.name}</span>
      </div>
      <p className="text-lg font-bold text-gray-900">{formatCompact(d.value)}</p>
    </div>
  );
};

const StatusRow = ({ name, count, percentage, colorConfig }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl ${colorConfig?.bg || "bg-gray-50"} border border-transparent hover:border-gray-200 transition-all`}>
    {colorConfig?.icon && <colorConfig.icon className={`w-4 h-4 ${colorConfig.text} flex-shrink-0`} strokeWidth={2} />}
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium ${colorConfig?.text || "text-gray-700"}`}>{name}</p>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-sm font-bold text-gray-900">{formatCompact(count)}</p>
      {percentage !== undefined && <p className="text-[10px] text-gray-400">{formatPercent(percentage)}</p>}
    </div>
    {percentage !== undefined && (
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(percentage || 0, 100)}%`, backgroundColor: colorConfig?.color || "#6b7280" }}
        />
      </div>
    )}
  </div>
);

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function NdrCodSection({ ndrData, codData, loading }) {
  const isNdrLoading = loading?.ndr;
  const isCodLoading = loading?.cod;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* NDR Section */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-orange-50">
              <AlertTriangle className="w-4.5 h-4.5 text-orange-500" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">NDR Management</h3>
              <p className="text-xs text-gray-400 mt-0.5">Non-Delivery Report status breakdown</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isNdrLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : ndrData ? (
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Donut */}
              <div className="xl:col-span-2 flex items-center justify-center">
                {(ndrData.ndr_status_counts || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={(ndrData.ndr_status_counts || []).map((s, i) => ({
                          name: s.status_display_name || s.status,
                          value: s.count,
                          fill: Object.values(NDR_COLORS)[i % Object.values(NDR_COLORS).length]?.color || CHART_COLORS[i],
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {(ndrData.ndr_status_counts || []).map((_, i) => (
                          <Cell key={i} fill={Object.values(NDR_COLORS)[i % Object.values(NDR_COLORS).length]?.color || CHART_COLORS[i]} stroke="white" strokeWidth={2} />
                        ))}
                      </Pie>
                      <Tooltip content={<GenericTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                    No NDR data
                  </div>
                )}
              </div>

              {/* Status List */}
              <div className="xl:col-span-3 space-y-2">
                {/* Total */}
                <div className="flex items-center justify-between p-3 bg-orange-50/50 rounded-xl border border-orange-100 mb-3">
                  <span className="text-sm font-semibold text-orange-700">Total NDR</span>
                  <span className="text-lg font-bold text-orange-800">{formatCompact(ndrData.total_ndr || 0)}</span>
                </div>

                {(ndrData.ndr_status_counts || []).map((s, i) => {
                  const colorKey = Object.keys(NDR_COLORS)[i % Object.keys(NDR_COLORS).length];
                  return (
                    <StatusRow
                      key={i}
                      name={s.status_display_name || s.status}
                      count={s.count}
                      percentage={s.percentage}
                      colorConfig={NDR_COLORS[colorKey]}
                    />
                  );
                })}

                {(ndrData.ndr_status_counts || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">No NDR records in this period</div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              NDR data unavailable
            </div>
          )}
        </div>
      </div>

      {/* COD Section */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-green-50">
              <Banknote className="w-4.5 h-4.5 text-green-500" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">COD Remittance Status</h3>
              <p className="text-xs text-gray-400 mt-0.5">Cash on delivery collection & settlement</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isCodLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : codData ? (
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {/* Donut */}
              <div className="xl:col-span-2 flex items-center justify-center">
                {(codData.cod_status_counts || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={(codData.cod_status_counts || []).map((s, i) => ({
                          name: s.status_display_name || s.status,
                          value: s.count || 0,
                          amount: s.amount || 0,
                          fill: Object.values(COD_COLORS)[i % Object.values(COD_COLORS).length]?.color || CHART_COLORS[i],
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {(codData.cod_status_counts || []).map((_, i) => (
                          <Cell key={i} fill={Object.values(COD_COLORS)[i % Object.values(COD_COLORS).length]?.color || CHART_COLORS[i]} stroke="white" strokeWidth={2} />
                        ))}
                      </Pie>
                      <Tooltip content={<GenericTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                    No COD data
                  </div>
                )}
              </div>

              {/* COD Stats */}
              <div className="xl:col-span-3 space-y-2">
                {/* Totals */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="p-3 bg-green-50/50 rounded-xl border border-green-100 text-center">
                    <p className="text-xs text-green-600 font-medium">Total COD</p>
                    <p className="text-lg font-bold text-green-800">{formatCurrency(codData.total_cod_amount || 0)}</p>
                  </div>
                  <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-center">
                    <p className="text-xs text-blue-600 font-medium">Remitted</p>
                    <p className="text-lg font-bold text-blue-800">{formatCurrency(codData.remitted_amount || 0)}</p>
                  </div>
                </div>

                {(codData.cod_status_counts || []).map((s, i) => {
                  const colorKey = Object.keys(COD_COLORS)[i % Object.keys(COD_COLORS).length];
                  return (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${COD_COLORS[colorKey]?.bg || "bg-gray-50"} transition-all`}>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COD_COLORS[colorKey]?.color }} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${COD_COLORS[colorKey]?.text || "text-gray-700"}`}>
                          {s.status_display_name || s.status}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-gray-900">{formatCompact(s.count || 0)}</p>
                        <p className="text-[10px] text-gray-400">{formatCurrency(s.amount || 0)}</p>
                      </div>
                    </div>
                  );
                })}

                {(codData.cod_status_counts || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">No COD records in this period</div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              COD data unavailable
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
